/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { TextField, Button, Modal, Backdrop, Fade, Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { ModalBase } from "../../utils/Interfaces";
import { createPasswordChangeRequest } from "./changePasswordFunctions";
import { delayedAction } from "../../utils/Functions";
import { useAppDispatch } from "../../app/hooks";

const RequestPasswordChangeModal = (props: ModalBase) => {
    const { open, handleClose } = props;

    const dispatch = useAppDispatch();

    const [userProfile, setUserProfile] = useState<string>("");
    const [message, setMessage] = useState<string>("");
    const [messageLevel, setMessageLevel] = useState<"success" | "error">("success");

    const [processing, setProcessing] = useState<boolean>(false);

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={() => {
                handleClose();
                void delayedAction(300, () => {
                    setMessage("");
                    setUserProfile("");
                    setMessageLevel("success");
                });
            }}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        backgroundColor: (theme) => theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: (theme) => theme.shadows[5],
                        padding: (theme) => theme.spacing(2, 4, 3),
                        zIndex: 1000,
                    }}
                >
                    {message.length === 0 && (
                        <Typography variant="h6" sx={{ textAlign: "center" }}>
                            Request Password Change
                        </Typography>
                    )}

                    {message.length > 0 && (
                        <Alert severity={messageLevel}>
                            <Typography>{message}</Typography>
                        </Alert>
                    )}

                    <Box sx={{ mb: 2 }} />

                    {message.length > 0 && messageLevel === "success" && (
                        <Button
                            fullWidth
                            disabled={userProfile.length === 0}
                            variant={"contained"}
                            onClick={() => {
                                handleClose();
                                void delayedAction(300, () => {
                                    setMessage("");
                                    setUserProfile("");
                                    setMessageLevel("success");
                                });
                            }}
                        >
                            Close
                        </Button>
                    )}

                    {((message.length > 0 && messageLevel !== "success") || message.length === 0) && (
                        <>
                            <TextField
                                onChange={(event) => {
                                    setUserProfile(event.target.value);
                                }}
                                margin="normal"
                                required
                                fullWidth
                                id="username_or_email"
                                label="Username or Email"
                                name="username_or_email"
                                value={userProfile}
                            />

                            {processing && (
                                <Stack direction="row" justifyContent={"center"}>
                                    <CircularProgress size={20} sx={{ mt: 1, mb: 1 }} />
                                </Stack>
                            )}

                            {!processing && (
                                <Button
                                    fullWidth
                                    disabled={userProfile.length === 0}
                                    variant={"contained"}
                                    onClick={() =>
                                        createPasswordChangeRequest(
                                            userProfile,
                                            dispatch,
                                            () => {
                                                setMessageLevel("success");
                                                setMessage(
                                                    "Password change request received, please check your email for further instructions."
                                                );
                                            },
                                            () => {
                                                setMessageLevel("error");
                                                setMessage(
                                                    "Password change request could not be created, please try again with your username. If your receive further errors, please contact the system administrator."
                                                );
                                            },
                                            setProcessing
                                        )
                                    }
                                >
                                    Submit
                                </Button>
                            )}
                        </>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default RequestPasswordChangeModal;
